import { useMutation, useQuery } from '@tanstack/react-query';
import { FlaskConical, Search, Send as SendIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { DeviceCell, DeviceStatusBadge, PermissionBadge } from '@/components/installations/device-sheet';
import { CopyButton } from '@/components/shared/copy-button';
import { LoadingButton } from '@/components/shared/loading-button';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/lib/api';
import { platformsReady } from '@/lib/credentials';
import type { AppDetail, Installation, Message, Page } from '@/lib/types';

/** The test-send `installation_ids` limit (packages/shared/src/schemas/sends.ts); the picker loads at most this many. */
const MAX_DEVICES = 200;

/** "Test and preview": send the draft to a few chosen test devices before it goes to everyone. */
export function TestSendDialog({ open, onOpenChange, app, message, onSent }: {
  open: boolean; onOpenChange(open: boolean): void; app: AppDetail;
  message: { title: string; body: string; data: Record<string, string>; action_url?: string };
  onSent(message: Message): void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  useEffect(() => { if (open) { setSelected(new Set()); setSearch(''); } }, [open]);

  const devices = useQuery({
    queryKey: ['installations', app.id, 'test-recipients'],
    queryFn: () => api<Page<Installation>>(`/admin/v1/apps/${app.id}/installations`, { query: { test_device: 'true', active: 'true', limit: MAX_DEVICES } }),
    enabled: open, staleTime: 0,
  });
  const all = devices.data?.data ?? [];
  // Client-side: the picker loads every active test device (up to 200) at once.
  const term = search.trim().toLowerCase();
  const list = !term ? all : all.filter((d) =>
    [d.test_device_name, d.device_model, d.external_user_id, d.installation_id, d.platform === 'ios' ? 'ios' : 'android', d.os_version]
      .some((v) => v?.toLowerCase().includes(term)));

  const ready = platformsReady(app);
  const selectable = list.filter((d) => ready[d.platform]);
  const reachableIds = new Set(all.filter((d) => ready[d.platform]).map((d) => d.installation_id));
  const unavailable = all.length - reachableIds.size;
  // What gets counted and sent: only ticked devices that can be reached. A disabled row (no credentials for its
  // platform) can never be in it, whatever was ticked before credentials changed or the list refreshed.
  const picked = [...selected].filter((id) => reachableIds.has(id));

  const send = useMutation({
    // One request: the API splits it into batches of 20 that form one message (P5.28).
    mutationFn: () => api<Message>(`/admin/v1/apps/${app.id}/test-send`, {
      method: 'POST',
      body: { to: { installation_ids: picked }, ...message },
    }),
    onSuccess: (m) => { onSent(m); onOpenChange(false); },
  });

  const toggle = (id: string, on: boolean) => setSelected((cur) => {
    const next = new Set(cur);
    if (on) next.add(id); else next.delete(id);
    return next;
  });
  // The header checkbox acts on the devices shown (after search), and keeps ticks made under another search.
  const shownIds = selectable.map((d) => d.installation_id);
  const shownChecked = shownIds.filter((id) => selected.has(id)).length;
  const allChecked = shownIds.length > 0 && shownChecked === shownIds.length;
  const toggleShown = () => setSelected((cur) => {
    const next = new Set(cur);
    if (allChecked) shownIds.forEach((id) => next.delete(id));
    else shownIds.forEach((id) => next.add(id));
    return next;
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!send.isPending) onOpenChange(o); }}>
      <DialogContent className="gap-0 p-0 sm:max-w-6xl">
        {/* pr-14 leaves room for the dialog's close button. */}
        <DialogHeader className="flex-row flex-wrap items-center gap-x-6 gap-y-3 border-b py-4 pr-14 pl-6">
          <div className="grid min-w-0 flex-1 gap-1.5">
            <DialogTitle>Test and preview</DialogTitle>
            <DialogDescription>Send this message only to the test devices you pick.</DialogDescription>
          </div>
          {all.length > 0 && (
            <div className="relative w-full sm:w-72">
              <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8"
                placeholder="Search test devices…" title="Name, model, External User ID or installation ID" aria-label="Search test devices"
              />
            </div>
          )}
        </DialogHeader>

        {devices.isLoading ? (
          <div className="grid gap-2 p-6"><Skeleton className="h-10" /><Skeleton className="h-10" /><Skeleton className="h-10" /></div>
        ) : all.length === 0 ? (
          <div className="grid justify-items-center gap-2 px-6 py-10 text-center text-sm">
            <FlaskConical className="size-6 text-muted-foreground" />
            <p className="font-medium">No test devices yet</p>
            <p className="text-muted-foreground">
              On <Link to={`/apps/${app.id}/installations`} className="font-medium text-foreground underline underline-offset-2">Devices</Link>,
              open a device's ⋯ menu and choose Add as test device.
            </p>
          </div>
        ) : (
          <>
            {/* Fixed height, so the popup doesn't jump in size while searching. */}
            <Table containerClassName="h-[min(60vh,34rem)] overflow-y-auto">
              {/* Each header cell is sticky with its own background (a sticky <thead> lets rows show above it in
                  Chrome), and the divider is a shadow because a sticky cell's border scrolls away with the rows. */}
              <TableHeader className="[&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-background [&_th]:shadow-[inset_0_-1px_0_var(--border)] [&_tr]:border-b-0">
                <TableRow>
                  <TableHead className="w-10 pl-6">
                    <Checkbox
                      aria-label={term ? 'Select all shown' : 'Select all'}
                      checked={allChecked ? true : shownChecked > 0 ? 'indeterminate' : false} disabled={shownIds.length === 0}
                      onCheckedChange={toggleShown}
                    />
                  </TableHead>
                  <TableHead>Device</TableHead>
                  <TableHead className="whitespace-nowrap">Installation ID</TableHead>
                  <TableHead className="whitespace-nowrap">External User ID</TableHead>
                  <TableHead>App / OS</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="pr-6">Permission</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.length === 0 && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">No test devices match “{search.trim()}”.</TableCell>
                  </TableRow>
                )}
                {list.map((d) => {
                  const id = d.installation_id;
                  const checked = selected.has(id);
                  const noCreds = !ready[d.platform];
                  const disabled = noCreds;
                  return (
                    <TableRow
                      key={d.id} data-state={checked ? 'selected' : undefined}
                      className={disabled ? 'opacity-60' : 'cursor-pointer'}
                      onClick={() => { if (!disabled) toggle(id, !checked); }}
                    >
                      <TableCell className="pl-6" onClick={(e) => e.stopPropagation()}>
                        <Checkbox aria-label={`Select ${d.test_device_name ?? d.device_model ?? id}`} checked={checked} disabled={disabled} onCheckedChange={(v) => toggle(id, !!v)} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap"><DeviceCell device={d} nameFirst /></TableCell>
                      <TableCell className="font-mono text-xs whitespace-nowrap">
                        {/* Copying an id must not also tick the row. */}
                        <span className="inline-flex items-center gap-2">{id} <span onClick={(e) => e.stopPropagation()}><CopyButton value={id} label={false} /></span></span>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{d.external_user_id ?? '—'}</TableCell>
                      <TableCell className="whitespace-nowrap">{[d.app_version, d.os_version].filter(Boolean).join(' / ') || '—'}</TableCell>
                      <TableCell><DeviceStatusBadge device={d} /></TableCell>
                      <TableCell className="pr-6 whitespace-nowrap">
                        {noCreds
                          ? <span className="text-xs text-muted-foreground">No {d.platform === 'ios' ? 'iOS' : 'Android'} credentials</span>
                          : <PermissionBadge value={d.notification_permission} />}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </>
        )}

        <DialogFooter className="border-t px-6 py-3 sm:items-center">
          {all.length > 0 && (
            <span className="mr-auto text-sm text-muted-foreground tabular-nums">
              {picked.length} of {all.length} selected
              {unavailable > 0 && <> · {unavailable} can't be selected (no credentials)</>}
              {term && <> · {list.length} of {all.length} shown</>}
            </span>
          )}
          <Button variant="outline" disabled={send.isPending} onClick={() => onOpenChange(false)}>Cancel</Button>
          <LoadingButton disabled={picked.length === 0} loading={send.isPending} onClick={() => send.mutate()}>
            <SendIcon /> Send to {picked.length || ''} {picked.length === 1 ? 'device' : 'devices'}
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
